import { AsyncPipe } from '@angular/common';
// prettier-ignore
import { Component, ElementRef, inject, input, output, TemplateRef, viewChild } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TCountingMethodType } from '@cpr/shared/counting-method';
import { combineLatest, debounceTime, Observable, startWith, switchMap, tap } from 'rxjs';
import { measurementCountFormControl } from '../form-controls/measurement-count-form-control';
import { IMeasurement } from '../measurement.interface';
import { MeasurementService } from '../measurement.service';
import { PermissionsModule } from '../../permissions/permissions.module';
import { ITaxon } from '../../taxon/taxon.interface';
import { TaxonService } from '../../taxon/taxon.service';
import { ISample } from '../../samples/interface/sample.interface';

@Component({
  standalone: true,
  selector: 'app-measurement-form',
  imports: [
    AsyncPipe,
    MatAutocompleteModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    PermissionsModule,
    ReactiveFormsModule,
  ],
  template: `
    <form
      [formGroup]="formGroup"
      [checkPermission]="['sample', 'analyse']"
      (submit)="addMeasurement()"
    >
      <div style="display: flex; gap: 16px; margin-bottom: 8px">
        <mat-form-field style="width: 100%">
          <mat-label>Taxon</mat-label>
          <input
            matInput
            formControlName="taxon"
            required
            [matAutocomplete]="taxonAutocomplete"
            #taxonInput
          />
          @if (formGroup.hasError('notAcceptedTaxon', 'taxon')) {
            <mat-error>{{ formGroup.getError('notAcceptedTaxon', 'taxon') }}</mat-error>
          }
        </mat-form-field>

        <mat-form-field style="width: 100%; max-width: 230px">
          <mat-label>Quantity</mat-label>
          <input matInput formControlName="quantity" required />
          @if (formGroup.hasError('invalidValue', 'quantity')) {
            <mat-error>Please enter a positive integer or '+'.</mat-error>
          }
        </mat-form-field>
      </div>

      <mat-form-field style="width: 100%">
        <mat-label>Comment</mat-label>
        <textarea matInput formControlName="comment" cdkTextareaAutosize></textarea>
      </mat-form-field>

      <button mat-flat-button [disabled]="formGroup.invalid" style="width: 100%">Add</button>
    </form>

    <mat-autocomplete #taxonAutocomplete="matAutocomplete" [displayWith]="getTaxonDisplayValue">
      @for (option of taxonSearch$ | async; track option._id) {
        @if (option.countingMethod === countingMethod()) {
          <mat-option [value]="option">
            <span>{{ option.taxonName }}</span>
            <span style="opacity: 50%"> (ID: {{ option.cprTaxonId }})</span>
            @if (option.taxonChildren?.length) {
              <span style="color: #00f8">{{ countingMethod() === 'PHYTO' ? '' : '(extra)' }}</span>
            }
          </mat-option>
        }
      }
    </mat-autocomplete>

    <ng-template #confirmAcceptedIdDialog>
      <h2 matDialogTitle>Taxon no longer in use</h2>
      <mat-dialog-content>
        <p>{{ formGroup.getError('notAcceptedTaxon', 'taxon') }}</p>
        <p>Do you wish to switch to the accepted taxa name?</p>
      </mat-dialog-content>
      <mat-dialog-actions>
        <button mat-button [matDialogClose]="false">Cancel</button>
        <button mat-flat-button [matDialogClose]="true">Yes</button>
      </mat-dialog-actions>
    </ng-template>
  `,
  styles: `
    .no-permission {
      display: none;
    }
  `,
})
export class MeasurementFormComponent {
  private readonly dialog = inject(MatDialog);
  private readonly measurementService = inject(MeasurementService);
  private readonly taxonService = inject(TaxonService);

  protected readonly countingMethod = input.required<TCountingMethodType>();
  protected readonly sample = input.required<ISample>();

  protected readonly measurementAdded = output<IMeasurement>();

  private readonly confirmAcceptedIdDialog =
    viewChild.required<TemplateRef<unknown>>('confirmAcceptedIdDialog');

  private readonly taxonInput = viewChild.required<ElementRef<HTMLInputElement>>('taxonInput');

  private readonly taxonFormControl = new FormControl<ITaxon | string>(undefined, {
    asyncValidators: async (control) => {
      const notATaxon = typeof control.value !== 'object';
      let notAcceptedTaxon: string | undefined;
      if (!notATaxon) {
        const acceptedTaxaChain = await this.taxonService.recursiveAcceptedTaxaLookup([
          control.value,
        ]);
        if (acceptedTaxaChain.length > 1) {
          const acceptedTaxon = acceptedTaxaChain.at(-1);
          notAcceptedTaxon = `The accepted name for "${control.value.taxonName}" is now "${acceptedTaxon.taxonName}".`;
          this.dialog
            .open(this.confirmAcceptedIdDialog(), { width: '400px' })
            .afterClosed()
            .subscribe((result) => result && this.taxonFormControl.setValue(acceptedTaxon));
        }
      }
      return notATaxon || notAcceptedTaxon ? { notATaxon, notAcceptedTaxon } : null;
    },
  });

  protected readonly formGroup = new FormGroup({
    taxon: this.taxonFormControl,
    quantity: measurementCountFormControl(),
    comment: new FormControl<string>(undefined),
  });

  protected readonly taxonSearch$: Observable<ITaxon[]> = combineLatest([
    toObservable(this.countingMethod).pipe(tap(() => this.formGroup.reset())),
    this.taxonFormControl.valueChanges,
  ]).pipe(
    debounceTime(200),
    switchMap(async ([countingMethod]) => {
      const { taxon } = this.formGroup.value;
      return typeof taxon !== 'string' ? [] : await this.taxonService.search(taxon, countingMethod);
    }),
    startWith([]),
  );

  getTaxonDisplayValue(value: ITaxon) {
    return value?.taxonChildren?.length
      ? `${value.taxonName} ${value.countingMethod === 'PHYTO' ? '' : '(extra)'}`
      : value?.taxonName;
  }

  async addMeasurement() {
    const { taxon } = this.formGroup.value;

    if (typeof taxon === 'string') {
      return;
    }

    const { comment, quantity } = this.formGroup.value;
    const analysedCount = quantity === '+' ? ('+' as const) : parseInt(quantity);

    this.measurementAdded.emit(
      await this.measurementService.setMeasurement({
        analysedCount,
        comment,
        sampleId: this.sample()._id,
        taxonId: taxon._id,
      }),
    );

    this.formGroup.reset();
    Object.values(this.formGroup.controls).forEach((control) => control.setErrors(null));

    setTimeout(() => this.taxonInput().nativeElement.focus());
  }
}
